<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { usePage } from "@inertiajs/vue3";

const STORAGE_KEY = "gifthero.siteNoticeDismissed";
const HEIGHT_PROPERTY = "--site-notice-height";

const page = usePage();
const message = computed(() => page.props.siteNotice || "");

const dismissedMessage = ref(null);
const bar = ref(null);
const isVisible = computed(
    () => message.value !== "" && dismissedMessage.value !== message.value,
);

let resizeObserver = null;

/**
 * The header is fixed, so the bar is fixed beneath it and `.main` is pushed
 * down by the bar's measured height (it wraps to several lines on phones).
 */
const syncHeight = () => {
    const height = isVisible.value && bar.value ? bar.value.offsetHeight : 0;
    document.documentElement.style.setProperty(HEIGHT_PROPERTY, `${height}px`);
};

const dismiss = () => {
    dismissedMessage.value = message.value;
    try {
        window.sessionStorage.setItem(STORAGE_KEY, message.value);
    } catch (e) {
        // Storage can be unavailable (private mode); the bar still hides for this page view.
    }
};

onMounted(() => {
    try {
        dismissedMessage.value = window.sessionStorage.getItem(STORAGE_KEY);
    } catch (e) {
        dismissedMessage.value = null;
    }

    if ("ResizeObserver" in window) {
        resizeObserver = new ResizeObserver(syncHeight);
    }
    watch(
        bar,
        (element, previousElement) => {
            if (previousElement) {
                resizeObserver?.unobserve(previousElement);
            }
            if (element) {
                resizeObserver?.observe(element);
            }
            syncHeight();
        },
        { immediate: true, flush: "post" },
    );
});

onBeforeUnmount(() => {
    resizeObserver?.disconnect();
    document.documentElement.style.removeProperty(HEIGHT_PROPERTY);
});
</script>

<template>
    <aside
        v-if="isVisible"
        ref="bar"
        class="site-notice"
        role="status"
        aria-label="Site notice"
    >
        <div class="site-notice__container container-wide">
            <p class="site-notice__message">{{ message }}</p>
            <button
                type="button"
                class="site-notice__dismiss"
                aria-label="Dismiss notice"
                @click="dismiss"
            >
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    aria-hidden="true"
                    focusable="false"
                >
                    <path
                        d="M1 1l12 12M13 1L1 13"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                    />
                </svg>
            </button>
        </div>
    </aside>
</template>
